<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Cropper, Preview } from 'vue-advanced-cropper'
import BButton from './bButton.vue'
import BModal from '@/components/common/BModal.vue'
import NumberInput from '@/components/common/NumberInput.vue'
import { useApplicationStore } from '@/config/store/applicationStore'
import 'vue-advanced-cropper/dist/style.css'

const props = withDefaults(defineProps<{
  image?: string
  aspectRatio?: number | null
  enableOutputSize?: boolean
  defaultOutputWidth?: number
  defaultOutputHeight?: number
}>(), {
  aspectRatio: null,
  enableOutputSize: false,
  defaultOutputWidth: 256,
  defaultOutputHeight: 256,
})

const emits = defineEmits<{
  'updated:image': [values: { dataUrl: string; canvas: HTMLCanvasElement }]
  'error': []
}>()

const { t } = useI18n()
const applicationStore = useApplicationStore()

const newImage = ref<HTMLImageElement | null>(null)
const newImageDataUrl = ref<string | null>(null)
const result = ref({
  coordinates: null,
  image: null,
  src: null as string | null,
})

const cropperComponent = ref<any>(null)
const outputWidth = ref(props.defaultOutputWidth)
const outputHeight = ref(props.defaultOutputHeight)
const syncingOutputSize = ref(false)

const resolvedAspectRatio = computed(() => {
  if (props.aspectRatio != null && props.aspectRatio > 0)
    return props.aspectRatio
  if (props.defaultOutputWidth > 0 && props.defaultOutputHeight > 0)
    return props.defaultOutputWidth / props.defaultOutputHeight
  return null
})

const onChange = ({ coordinates, image }) => {
  result.value.coordinates = coordinates
  result.value.image = image
  const { canvas } = cropperComponent.value.getResult()
  const src = canvas.toDataURL()
  result.value.src = src
}

const isModalOpen = ref(false)

const clampSize = (value: number) => Math.min(4096, Math.max(16, Math.round(value || 0)))

const syncHeightFromWidth = (width: number) => {
  if (!resolvedAspectRatio.value) return
  syncingOutputSize.value = true
  outputHeight.value = clampSize(width / resolvedAspectRatio.value)
  syncingOutputSize.value = false
}

const syncWidthFromHeight = (height: number) => {
  if (!resolvedAspectRatio.value) return
  syncingOutputSize.value = true
  outputWidth.value = clampSize(height * resolvedAspectRatio.value)
  syncingOutputSize.value = false
}

watch(outputWidth, (width) => {
  if (syncingOutputSize.value || !resolvedAspectRatio.value) return
  syncHeightFromWidth(clampSize(width))
})

watch(outputHeight, (height) => {
  if (syncingOutputSize.value || !resolvedAspectRatio.value) return
  syncWidthFromHeight(clampSize(height))
})

watch(() => props.image, (value) => {
  if (!value) {
    isModalOpen.value = false
    newImageDataUrl.value = null
    return
  }

  syncingOutputSize.value = true
  outputWidth.value = props.defaultOutputWidth
  outputHeight.value = props.defaultOutputHeight
  if (resolvedAspectRatio.value)
    outputHeight.value = clampSize(outputWidth.value / resolvedAspectRatio.value)
  syncingOutputSize.value = false
  newImage.value = new Image()
  newImage.value.crossOrigin = 'Anonymous'
  newImage.value.onload = () => {
    const canvas = document.createElement('canvas')
    canvas.width = newImage.value!.naturalWidth
    canvas.height = newImage.value!.naturalHeight
    const ctx = canvas.getContext('2d')
    ctx?.drawImage(newImage.value!, 0, 0, canvas.width, canvas.height)
    newImageDataUrl.value = canvas.toDataURL()
    isModalOpen.value = true
  }
  // Not a decodable image (e.g. a PDF): don't open an empty modal
  newImage.value.onerror = () => {
    newImageDataUrl.value = null
    isModalOpen.value = false
    applicationStore.pushError({
      message: t('components.common.fileDrop.errors.invalidImage'),
      duration: 3000,
      color: 'error',
    })
    emits('error')
  }

  newImage.value.src = value
})

const resizeCanvas = (source: HTMLCanvasElement, width: number, height: number) => {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) return source
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(source, 0, 0, width, height)
  return canvas
}

const retreiveCroppedImage = async () => {
  if (!cropperComponent.value) return
  const { canvas } = cropperComponent.value.getResult()
  let output = canvas as HTMLCanvasElement

  if (props.enableOutputSize) {
    const width = clampSize(outputWidth.value)
    const height = clampSize(outputHeight.value)
    outputWidth.value = width
    outputHeight.value = height
    output = resizeCanvas(canvas, width, height)
  }

  emits('updated:image', { dataUrl: output.toDataURL('image/png'), canvas: output })
  isModalOpen.value = false
}

const defaultSize = ({ imageSize }) => {
  const ratio = resolvedAspectRatio.value
  if (!ratio) {
    return {
      width: imageSize.width,
      height: imageSize.height,
    }
  }

  // Keep the stencil inside the image bounds (critical for wide ratios like 21:9).
  const width = Math.min(imageSize.width, imageSize.height * ratio)
  const height = width / ratio

  return { width, height }
}
</script>

<template>
  <BModal v-model="isModalOpen">
    <template #default>
      <div v-if="newImageDataUrl" class="flex gap-2 flex-wrap">
        <div
          class="mx-auto w-full"
          :class="resolvedAspectRatio && resolvedAspectRatio > 1.4 ? 'max-w-[720px]' : 'max-w-[500px]'"
        >
          <Cropper
            ref="cropperComponent"
            class="cropper"
            :src="newImageDataUrl"
            :debounce="false"
            :stencil-props="{
              aspectRatio: resolvedAspectRatio,
            }"
            :default-size="defaultSize"
            @change="onChange"
          />
        </div>

        <div class="flex flex-col bg-page p-4 grow gap-4 rounded-sm">
          <slot name="examples" :result="result">
            <div class="card-base flex gap-5">
              <Preview
                class="rounded-full shrink-0"
                :width="90"
                :height="90"
                :image="result.image"
                :coordinates="result.coordinates"
              />
              <div class="flex flex-col gap-2">
                <p class="font-bold">
                  Exemple
                </p>
                <p class="text-primary-400 italic underline">
                  exemple@exemple.com
                </p>
              </div>
            </div>
            <div class="flex gap-2 flex-wrap">
              <div v-for="item in 2" :key="item" class="card-base flex flex-col gap-5 w-52 grow">
                <div class="flex gap-2">
                  <Preview
                    :width="40"
                    :height="40"
                    class="rounded-full shrink-0"
                    :image="result.image"
                    :coordinates="result.coordinates"
                  />
                  <p class="font-bold">
                    Exemple
                  </p>
                </div>
                <p>Lorem ipsum dolor sit amet consectetur adipisicing elit. Quia beatae earum ea natus voluptates dolor commodi sit!</p>
              </div>
            </div>
          </slot>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex flex-wrap items-end justify-between gap-3 w-full">
        <div v-if="enableOutputSize" class="flex flex-wrap items-end gap-3">
          <NumberInput
            v-model="outputWidth"
            :label="t('modules.management.account.edit.logoOutputWidth')"
            class="w-28"
          />
          <NumberInput
            v-model="outputHeight"
            :label="t('modules.management.account.edit.logoOutputHeight')"
            class="w-28"
          />
          <p class="subtitle text-xs pb-2">
            {{ t('modules.management.account.edit.logoOutputHint') }}
          </p>
        </div>
        <div class="flex justify-end gap-2 ml-auto">
          <BButton bg="base" @click="isModalOpen = false">
            {{ t('common.cancel') }}
          </BButton>
          <BButton @click="retreiveCroppedImage">
            {{ t('common.save') }}
          </BButton>
        </div>
      </div>
    </template>
  </BModal>
</template>
